import { z } from 'zod';
import { LegalFormSchema, type LegalForm } from './company.schemas';
import { refersTo } from '../common/refers-to';

// Lifecycle status of a Standort partner-onboarding record. Only `invited` is
// set in IMM-255.2 (on trigger); the remaining states are driven by the partner
// wizard + operator review in IMM-255.3/255.4. The full union is defined now so
// the contract is stable for those slices. UPPER_CASE is reserved for
// domain-constant codes; these lifecycle states use the lower_snake form the
// product brief labels them with (Eingeladen · In Bearbeitung · …).
export const OrganizationOnboardingStatusSchema = z.enum([
  'invited',
  'in_progress',
  'submitted',
  'in_review',
  'changes_requested',
  'approved',
  'rejected',
]);

export type OrganizationOnboardingStatus = z.infer<typeof OrganizationOnboardingStatusSchema>;

const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http(s) URL' });

const optionalHttpUrl = z.preprocess(emptyToNull, HttpUrlSchema.nullable()).default(null);

// The partner onboarding wizard steps, in order (IMM-257). `legal` (Rechtliche
// Angaben) → `contact` (Kontakt) → `consents` (Einwilligungen). Used as the
// per-step draft-save discriminator + the resume pointer the wizard hydrates.
export const OnboardingStepSchema = z.enum(['legal', 'contact', 'consents']);

export type OnboardingStep = z.infer<typeof OnboardingStepSchema>;

// German USt-IdNr: `DE` followed by exactly 9 digits. The contract-boundary
// guard only; the user-facing German message lives in the web-app.
const GermanVatIdSchema = z
  .string()
  .trim()
  .regex(/^DE[0-9]{9}$/, { message: 'must be DE followed by 9 digits' });

// Optional Anschrift for the partner Standort shell. Mirrors the lenient
// create-organization address shape; nullable as a whole because the partner
// supplies the firm's address during their own onboarding.
const TriggerPartnerOnboardingAddressSchema = z.object({
  street: z.string().trim().min(1).max(100),
  houseNumber: z.string().trim().min(1).max(20),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(1).max(80),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});

export const TriggerPartnerOnboardingPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const TriggerPartnerOnboardingBodySchema = z.object({
  // Optional parent Standort; defaults to the workspace's main organisation
  // when omitted.
  parentId: z.uuid().nullable().default(null),
  name: z.string().trim().min(1).max(80),
  address: z.preprocess((value) => value ?? null, TriggerPartnerOnboardingAddressSchema.nullable()).default(null),
  internalWebsite: optionalHttpUrl,
  // The designated location manager (user-facing label: "Standortleitung").
  // Captured as two fields rather than one display name that gets split on
  // whitespace: the split handed a single-word entry an empty last name, and it
  // reached the members table through MemberCreator without ever passing the
  // `.min(1)` every other member write enforces (IMM-426). Same bounds as
  // `CreateWorkspaceMemberBodySchema`, which is where this manager eventually
  // lands. The e-mail is RFC 5322 validated (Zod's `.email()` implements the
  // practical RFC 5322 addr-spec).
  locationManagerFirstName: z.string().trim().min(1).max(80),
  locationManagerLastName: z.string().trim().min(1).max(80),
  locationManagerEmail: z.email().max(254),
});

export type TriggerPartnerOnboardingBody = z.infer<typeof TriggerPartnerOnboardingBodySchema>;

export const TriggerPartnerOnboardingResponseSchema = z.object({
  id: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  status: OrganizationOnboardingStatusSchema,
  // The manager's display name, composed from the two stored name parts --
  // read-only, for display. Callers that need the parts read them from the
  // onboarding record itself.
  locationManagerName: z.string().min(1),
  locationManagerEmail: z.string().min(1),
  createdAt: z.iso.datetime(),
});

export type TriggerPartnerOnboardingResponse = z.infer<typeof TriggerPartnerOnboardingResponseSchema>;

// ---------------------------------------------------------------------------
// IMM-255.3 — partner-driven lifecycle over an IdP (WorkOS) session.
//
// The magic link is exchanged once for a real IdP session: the exchange verifies
// the token hash-only, mints a WorkOS session for the onboarding's location
// manager (Magic Auth) server-side, returns the access token + the `onboardingId`
// the token mapped to in memory, and sets the standard refresh cookie. Every
// subsequent partner call carries that bearer access token and addresses the
// onboarding by id in the path. The lifecycle routes are authorised by the
// `requireOnboardingParticipant` middleware (permission
// `organization:identity:partner-onboard`), which loads the path `:onboardingId` and then
// authorises by *ownership* — the authenticated user's e-mail MUST equal the
// onboarding's location-manager e-mail — so a partner can only ever touch an
// onboarding they are the location manager of (a tampered/other id fails the
// ownership check → 403).
// ---------------------------------------------------------------------------

export const OnboardingPathParamsSchema = z.object({ onboardingId: z.uuid() });

export type OnboardingPathParams = z.infer<typeof OnboardingPathParamsSchema>;

// The required consent set for the partner onboarding V1 (IMM-257.4): a single
// data-protection consent. Exported from the contract so the consents step, the
// web-app render, and the submit gate (IMM-257.5) all share the same source of
// truth for which keys must be granted.
export const REQUIRED_PARTNER_CONSENT_KEYS = ['data-protection'] as const;

export type PartnerConsentKey = (typeof REQUIRED_PARTNER_CONSENT_KEYS)[number];

// One consent the partner grants/declines on the consents step / on submit.
// `key` is generic content (the required consent KEYS are above) — stored
// revision-safe as submitted; `grantedAt` + `grantedBy` are stamped server-side,
// never accepted from the client. `grantedByName` is the user-typed name of the
// granting person and IS part of the payload: it is required (non-empty) when
// `granted === true`, and may be null when `granted === false`. It must not be
// conflated with the server-set `grantedBy` (the location manager's e-mail).
export const ConsentInputSchema = z
  .object({
    key: z.string().trim().min(1).max(120),
    granted: z.boolean(),
    grantedByName: z.string().trim().max(200).nullable().default(null),
  })
  .refine((value) => !value.granted || (value.grantedByName !== null && value.grantedByName.length > 0), {
    message: "granting person's name is required when granting",
    path: ['grantedByName'],
  });

export type ConsentInput = z.infer<typeof ConsentInputSchema>;

// The persisted/returned shape of a stored consent record: the granted flag, the
// user-typed granting-person name, the server-set timestamp, and the granting
// person (`grantedBy` = the location manager's e-mail — the partner has no
// account).
export const ConsentRecordSchema = z.object({
  key: z.string().min(1),
  granted: z.boolean(),
  grantedByName: z.string().nullable(),
  grantedAt: z.iso.datetime(),
  grantedBy: z.string().min(1),
});

export type ConsentRecordView = z.infer<typeof ConsentRecordSchema>;

// Step 1 of the magic-link session flow (ADR 0030): body carries the raw
// magic-link token; the handler verifies it hash-only and asks WorkOS to
// e-mail a one-time Magic Auth code to the onboarding's location manager. The
// raw token is verified here only — never stored, logged, or returned.
export const RequestOnboardingSessionCodeBodySchema = z.object({
  token: z.string().trim().min(1),
});

export type RequestOnboardingSessionCodeBody = z.infer<typeof RequestOnboardingSessionCodeBodySchema>;

// Step 2: body carries the same magic-link token plus the one-time code WorkOS
// e-mailed in step 1. The handler re-verifies the token hash and redeems the
// code for a real WorkOS session.
export const ExchangeOnboardingSessionBodySchema = z.object({
  token: z.string().trim().min(1),
  code: z.string().trim().min(1),
});

export type ExchangeOnboardingSessionBody = z.infer<typeof ExchangeOnboardingSessionBodySchema>;

// The inviting workspace's branding, fronted on every partner-facing surface so
// the partner is onboarded *by that workspace*, not by immoOS (IMM-223 substrate,
// IMM-257.1). `wordmark` is server-defaulted to the workspace name when unset, so
// it is always a usable label; `logoUrl`/`brandColour` are null until an operator
// sets them. immoOS recedes to a subtle „läuft auf immoOS" platform mark.
export const WorkspaceBrandingSchema = z.object({
  workspaceName: z.string().min(1),
  wordmark: z.string().min(1),
  // Rendered into an <img src> on the partner surfaces, so it is URL-validated at
  // the contract boundary rather than emitting a broken image. `brandColour` stays
  // a lenient string: it is only ever used as a CSS custom-property value, so a
  // malformed value degrades to "no accent" instead of breaking render — the
  // operator-input format check lives with the branding editor (IMM-223).
  logoUrl: HttpUrlSchema.nullable(),
  brandColour: z.string().nullable(),
});

export type WorkspaceBranding = z.infer<typeof WorkspaceBrandingSchema>;

// Step 1 response: the id of the onboarding the token mapped to (so the FE can
// label the code-entry screen) and the inviting workspace's branding (fronted
// before any session exists). No access token — the code has only just been
// e-mailed, not redeemed yet.
export const RequestOnboardingSessionCodeResponseSchema = z.object({
  onboardingId: z.uuid(),
  branding: WorkspaceBrandingSchema,
});

export type RequestOnboardingSessionCodeResponse = z.infer<typeof RequestOnboardingSessionCodeResponseSchema>;

// Exchange response: the WorkOS access token (kept in memory by the SPA), the
// id of the onboarding the token mapped to (so the wizard knows which onboarding
// to address on the lifecycle routes), and the inviting workspace's branding (so
// every post-exchange surface — including the read-only already-submitted screen
// where the lifecycle routes 403 — can front the workspace). The refresh token is
// sealed into the standard `__Secure-immoos_refresh` cookie by the presenter and
// never appears in the body — mirrors `/v1/auth/callback`.
export const ExchangeOnboardingSessionResponseSchema = z.object({
  accessToken: z.string().min(1),
  onboardingId: z.uuid(),
  branding: WorkspaceBrandingSchema,
});

export type ExchangeOnboardingSessionResponse = z.infer<typeof ExchangeOnboardingSessionResponseSchema>;

// Partner session refresh response: a freshly-rotated WorkOS access token the
// SPA swaps into its in-memory store. Mirrors the member `/v1/auth/refresh`
// `{ accessToken }` shape — the rotated refresh token is re-sealed into the
// partner refresh cookie by the presenter and never appears in the body.
export const RefreshOnboardingSessionResponseSchema = z.object({
  accessToken: z.string().min(1),
});

export type RefreshOnboardingSessionResponse = z.infer<typeof RefreshOnboardingSessionResponseSchema>;

// The partner's proposed Company values, supplied on submit (IMM-255.4). Mirrors
// the writable Company fields (legal identity + contact + the §34c/§18 Impressum
// fields) plus a raw address — all fields nullable because the wizard (IMM-257)
// fills them incrementally and a partial submission is valid (lead-/partner-facing
// inputs stay lenient). This is the *proposed* data, parked on the onboarding
// record until the operator approves; nothing reaches the Company before approval.
const SubmittedAddressSchema = z.object({
  street: z.string().trim().max(100).nullable().default(null),
  houseNumber: z.string().trim().max(20).nullable().default(null),
  postalCode: z.string().trim().max(10).nullable().default(null),
  city: z.string().trim().max(80).nullable().default(null),
  country: z.string().trim().max(80).nullable().default(null),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});

export type SubmittedAddress = z.infer<typeof SubmittedAddressSchema>;

const emptyToNullText = (max: number) => z.preprocess(emptyToNull, z.string().trim().max(max).nullable()).default(null);

export const SubmittedCompanyDataSchema = z.object({
  companyName: emptyToNullText(120),
  legalForm: z.preprocess(emptyToNull, LegalFormSchema.nullable()).default(null),
  managingDirectors: emptyToNullText(240),
  commercialRegisterCourt: emptyToNullText(120),
  commercialRegisterNumber: emptyToNullText(40),
  // Format-validated only when provided (stays nullable for incremental drafts):
  // a German USt-IdNr is `DE` + 9 digits. An empty/absent value is null.
  vatId: z.preprocess(emptyToNull, GermanVatIdSchema.nullable()).default(null),
  supervisoryAuthority: emptyToNullText(200),
  professionalIndemnityInsurance: emptyToNullText(200),
  tradePermitAuthority: emptyToNullText(200),
  contentResponsiblePerson: emptyToNullText(200),
  // http(s) URL when provided, null otherwise — reuses the file's URL helper.
  website: optionalHttpUrl,
  phone: emptyToNullText(40),
  email: emptyToNullText(254),
  address: z.preprocess((value) => value ?? null, SubmittedAddressSchema.nullable()).default(null),
});

export type SubmittedCompanyData = z.infer<typeof SubmittedCompanyDataSchema>;

// Encode-safe RESPONSE mirror of `SubmittedCompanyData`. The response serializer
// (fastify-type-provider-zod v7) ENCODES responses via `safeEncode`, and the
// draft/input `SubmittedCompanyDataSchema` above is built from unidirectional
// `z.preprocess` transforms (`emptyToNull`, …) that throw `ZodEncodeError` on
// encode. The `emptyToNull` coercion is an INPUT concern anyway: data leaving the
// API is already normalised, so a plain nullable mirror serialises cleanly. The
// type-equality guard below locks it to the canonical `SubmittedCompanyData`
// output shape so the two can never drift.
const SubmittedAddressResponseSchema = z.object({
  street: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

export const SubmittedCompanyDataResponseSchema = z.object({
  companyName: z.string().nullable(),
  legalForm: LegalFormSchema.nullable(),
  managingDirectors: z.string().nullable(),
  commercialRegisterCourt: z.string().nullable(),
  commercialRegisterNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  supervisoryAuthority: z.string().nullable(),
  professionalIndemnityInsurance: z.string().nullable(),
  tradePermitAuthority: z.string().nullable(),
  contentResponsiblePerson: z.string().nullable(),
  website: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: SubmittedAddressResponseSchema.nullable(),
});

// Compile-time guard: the response mirror must be exactly the canonical
// `SubmittedCompanyData` output shape (assignable both ways), so the wizard's
// hydration and the domain handler keep seeing an identical type.
type _SubmittedCompanyDataResponseMatches =
  z.infer<typeof SubmittedCompanyDataResponseSchema> extends SubmittedCompanyData
    ? SubmittedCompanyData extends z.infer<typeof SubmittedCompanyDataResponseSchema>
      ? true
      : never
    : never;
const _submittedCompanyDataResponseMatches: _SubmittedCompanyDataResponseMatches = true;
void _submittedCompanyDataResponseMatches;

// The scoped onboarding state the partner sees over their session. NEVER
// includes the token hash. Mirrors the lifecycle fields the wizard needs.
// `reviewNotes` carries the operator's note when the onboarding is in
// `changes_requested`, so the wizard can show the changes-requested banner.
// `currentStep` is the non-regressing resume pointer (null before the first
// draft save); `companyData` is the draft snapshot so the wizard can hydrate
// the form on reload (all-null when nothing has been drafted).
export const OnboardingMeResponseSchema = z.object({
  id: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  status: OrganizationOnboardingStatusSchema,
  // The manager's display name, composed from the two stored name parts --
  // read-only, for display. Callers that need the parts read them from the
  // onboarding record itself.
  locationManagerName: z.string().min(1),
  locationManagerEmail: z.string().min(1),
  consents: z.array(ConsentRecordSchema),
  submittedAt: z.iso.datetime().nullable(),
  reviewNotes: z.string().nullable(),
  currentStep: OnboardingStepSchema.nullable(),
  companyData: SubmittedCompanyDataResponseSchema,
});

export type OnboardingMeResponse = z.infer<typeof OnboardingMeResponseSchema>;

export const StartOnboardingResponseSchema = OnboardingMeResponseSchema;
export type StartOnboardingResponse = z.infer<typeof StartOnboardingResponseSchema>;

// One per-step draft save (IMM-257). The wizard sends which `step` it is saving
// plus the partial `companyData` gathered so far (all fields nullable). The
// step's draft schema is verified (vat/url format guarded when provided), the
// draft snapshot REPLACES the parked company-data, and the resume pointer
// advances (never regresses). Reused by the Kontakt step (IMM-257.3).
export const SaveDraftBodySchema = z.object({
  step: OnboardingStepSchema,
  companyData: SubmittedCompanyDataSchema,
  // The consents the partner granted/declined on the consents step (IMM-257.4).
  // The other steps send `[]`; the consents step sends the required set. Each is
  // stamped server-side (grantedAt + grantedBy) and REPLACES the stored consents.
  consents: z.array(ConsentInputSchema).default([]),
});

export type SaveDraftBody = z.infer<typeof SaveDraftBodySchema>;

export const SubmitOnboardingBodySchema = z.object({
  consents: z.array(ConsentInputSchema).default([]),
  // The partner's proposed company data (IMM-255.4). Optional as a whole — a
  // submission without it is valid; defaults to an all-null payload.
  companyData: z.preprocess((value) => value ?? null, SubmittedCompanyDataSchema.nullable()).default(null),
});

export type SubmitOnboardingBody = z.infer<typeof SubmitOnboardingBodySchema>;

export const SubmitOnboardingResponseSchema = OnboardingMeResponseSchema;
export type SubmitOnboardingResponse = z.infer<typeof SubmitOnboardingResponseSchema>;

// One missing required item on a strict submission (IMM-257.5): which wizard
// step it lives on and which field is absent (or, for consents, the missing
// required consent key). The web-app routes the GF to `step` and field-marks
// `field`; the backend submit gate throws when any are present.
export type SubmissionIssue = { readonly step: OnboardingStep; readonly field: string };

// The steps in wizard order; the strict gate reports issues in this order so the
// caller can route to the FIRST offending step. Single source of truth for the
// step ordering used by both the validator and the web-app router.
export const FIRST_OFFENDING_STEP: ReadonlyArray<OnboardingStep> = ['legal', 'contact', 'consents'];

// Legal forms entered in the Handelsregister. The Handelsregister (Gericht) is a
// required legal-step field only for these; the non-registered forms
// (Einzelunternehmen, GbR, PartG mbB) have no Handelsregistereintrag, so it must
// not block their submission (IMM-347). PartG mbB sits in the Partnerschafts-
// register rather than the Handelsregister and is treated as non-registered here.
const HANDELSREGISTER_LEGAL_FORMS: ReadonlySet<LegalForm> = new Set([
  'GMBH',
  'UG',
  'AG',
  'EK',
  'OHG',
  'KG',
  'GMBH_CO_KG',
  'UG_CO_KG',
]);

// Whether the selected Rechtsform is entered in the Handelsregister (IMM-347).
// Shared by the submission gates and the web-app form so the required-marker and
// the gate can never drift apart.
export function isHandelsregisterLegalForm(legalForm: LegalForm | null | undefined): boolean {
  return legalForm != null && HANDELSREGISTER_LEGAL_FORMS.has(legalForm);
}

// Legal-step fields required for a strict submission regardless of Rechtsform
// (IMM-257.5). The Handelsregister (Gericht) is required conditionally — added by
// `requiredLegalFields` for Handelsregister forms only (IMM-347). Intentionally
// absent: `website` and `commercialRegisterNumber` (IMM-325), `vatId` (IMM-308),
// and `professionalIndemnityInsurance` (IMM-347) — none block submission; vatId
// stays format-checked (DE + 9 digits) only when filled.
const ALWAYS_REQUIRED_LEGAL_FIELDS = [
  'companyName',
  'legalForm',
  'managingDirectors',
  'tradePermitAuthority',
  'supervisoryAuthority',
  'contentResponsiblePerson',
] as const satisfies ReadonlyArray<keyof SubmittedCompanyData>;

// The legal-step required-field set for the given Rechtsform (IMM-347): the
// always-required fields, plus the Handelsregister (Gericht) when the form is
// entered in the Handelsregister. Single source of truth shared by the partner
// (`submittableIssues`) and company (`companySubmittableIssues`) gates and the
// web-app form.
export function requiredLegalFields(
  legalForm: LegalForm | null | undefined,
): ReadonlyArray<keyof SubmittedCompanyData> {
  return isHandelsregisterLegalForm(legalForm)
    ? [...ALWAYS_REQUIRED_LEGAL_FIELDS, 'commercialRegisterCourt']
    : ALWAYS_REQUIRED_LEGAL_FIELDS;
}

// Required address parts for the contact step (lat/lng stay optional).
const REQUIRED_ADDRESS_FIELDS = ['street', 'houseNumber', 'postalCode', 'city', 'country'] as const;

function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === '';
}

/**
 * The strict-submission gate (IMM-257.5). Pure: returns the missing required
 * items in step order (legal → contact → consents), empty when the package is
 * submittable. Shared by the backend submit gate (throws when non-empty) and the
 * web-app (routes to the first offending step + field-marks). Required set:
 * - legal: every field in `requiredLegalFields(legalForm)` present (non-blank) —
 *   the Handelsregister (Gericht) only for Handelsregister forms; `website`,
 *   `commercialRegisterNumber`, `vatId`, and `professionalIndemnityInsurance` are
 *   optional.
 * - contact: `email`, `phone`, and an `address` with non-empty street, house
 *   number, postal code, city, and country (lat/lng optional).
 * - consents: every `REQUIRED_PARTNER_CONSENT_KEYS` key present AND granted.
 */
export function submittableIssues(
  companyData: SubmittedCompanyData | null,
  consents: ReadonlyArray<ConsentInput>,
): SubmissionIssue[] {
  const issues: SubmissionIssue[] = [];

  for (const field of requiredLegalFields(companyData?.legalForm)) {
    if (isBlank(companyData?.[field] as string | null | undefined)) {
      issues.push({ step: 'legal', field });
    }
  }

  if (isBlank(companyData?.email)) {
    issues.push({ step: 'contact', field: 'email' });
  }

  if (isBlank(companyData?.phone)) {
    issues.push({ step: 'contact', field: 'phone' });
  }

  for (const part of REQUIRED_ADDRESS_FIELDS) {
    if (isBlank(companyData?.address?.[part])) {
      issues.push({ step: 'contact', field: `address.${part}` });
    }
  }

  for (const key of REQUIRED_PARTNER_CONSENT_KEYS) {
    const consent = consents.find((candidate) => candidate.key === key);

    if (consent === undefined || !consent.granted) {
      issues.push({ step: 'consents', field: key });
    } else if (isBlank(consent.grantedByName)) {
      // A granted consent without its granting-person name is not submittable —
      // surfaced as a consents-step issue so the gate/routing catch it (and the
      // name field shows its message) rather than the API rejecting with a 400.
      issues.push({ step: 'consents', field: 'grantedByName' });
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// IMM-255.4 — operator review + decision (workspace-scoped, member-authed).
//
// These routes hang off the workspace org path
// `/v1/workspaces/:workspaceId/organizations/:organizationId/onboarding/*` and are
// gated by `requireWorkspaceMembership` + the `organization:identity:organization-update`
// permission (approver authority = whoever may create a Standort). The handler
// resolves the onboarding by the organisation id and verifies workspace tenancy,
// so an operator can only review onboardings inside their own workspace.
// ---------------------------------------------------------------------------

export const OnboardingReviewPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
});

export type OnboardingReviewPathParams = z.infer<typeof OnboardingReviewPathParamsSchema>;

// Notes the reviewer leaves on a change request — surfaced to the partner in the
// change-request mail + on the re-opened wizard. Required (a change request
// without an explanation is not useful).
export const RequestOnboardingChangesBodySchema = z.object({
  notes: z.string().trim().min(1).max(2000),
});

export type RequestOnboardingChangesBody = z.infer<typeof RequestOnboardingChangesBodySchema>;

// The operator-facing view of an onboarding after a review decision. Carries the
// lifecycle fields the review surface needs, the partner's proposed
// submittedCompanyData (what the operator approves), plus the review-only fields
// (reviewNotes, approvedAt). NEVER includes the token hash.
export const OnboardingReviewResponseSchema = z.object({
  id: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  workspaceId: z.uuid(),
  status: OrganizationOnboardingStatusSchema,
  // The manager's display name, composed from the two stored name parts --
  // read-only, for display. Callers that need the parts read them from the
  // onboarding record itself.
  locationManagerName: z.string().min(1),
  locationManagerEmail: z.string().min(1),
  consents: z.array(ConsentRecordSchema),
  createdAt: z.iso.datetime(),
  // The magic link's expiry (14-day reusable window, IMM-255.3) — null until a
  // link has been issued (e.g. a record still in `invited` with no deliverable
  // resolved yet).
  expiresAt: z.iso.datetime().nullable(),
  submittedAt: z.iso.datetime().nullable(),
  // The partner's proposed company data the operator approves — null before
  // submit. The encode-safe response mirror of the submitted shape, so the
  // reviewer sees exactly what was sent.
  submittedCompanyData: SubmittedCompanyDataResponseSchema.nullable(),
  reviewNotes: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
});

export type OnboardingReviewResponse = z.infer<typeof OnboardingReviewResponseSchema>;
